import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { testTemplateAPI } from '../../../api/testTemplateApi';
import { testDataAPI } from '../../../api/testDataApi';
import { testReportAPI } from '../../../api/testReportApi';
import * as S from '../../Dashboard/Dashboard.styles';
import * as T from './TestManagement.styles';
import { getHighestRole, canCreateTestTemplate, canManageTestBatches, canEditTestTemplate, canDeleteTestTemplate, canEditTestResponse, canDeleteTestResponse, canGenerateTestReport, canViewTestRanking, canManageReportQueue, canViewArchivedResponses, canArchiveResponses, canViewTestTemplate } from '../../../utils/portalPermissions';
import { listCampuses } from '../../../api/campusPortalApi';
import { isTestDemoCampusId, withoutTestDemoCampus } from '../../../utils/testDemoCampus';
import { getMyProfile } from '../../../api/userProfileApi';
import SearchableSelect from '../../../components/Portal/SearchableSelect';
import ChipSelect from '../../../components/Portal/ChipSelect';

// No template currently has a campusId set, so the filter has nothing to filter yet.
// Flip to true once templates start getting assigned to a campus.
const SHOW_CAMPUS_FILTER = false;

// Ranking modal: keep the header row pinned while scrolling so it stays above each student's row in screenshots.
// The box-shadow re-draws the bottom border, which border-collapse would otherwise scroll away with the rows.
const rankingStickyTh = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#f8f9fa',
  boxShadow: '0 1px 0 #dee2e6',
};

const TestManagement = () => {
  const auth = useAuth();  const navigate = useNavigate();  const [testTemplates, setTestTemplates] = useState([]);
  // const [tests, setTests] = useState([]);
  const [testResponses, setTestResponses] = useState([]);
  // const [newTest, setNewTest] = useState({ name: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedTemplates, setExpandedTemplates] = useState(true); // Default expanded for faster perceived load
  const [expandedResponsesByTab, setExpandedResponsesByTab] = useState({}); // Default collapsed; data loads in background
  const [disabledReportButtons, setDisabledReportButtons] = useState(new Set()); // Local disable state for group report buttons
  const [reportQueue, setReportQueue] = useState([]);
  const [previousQueueData, setPreviousQueueData] = useState([]); // eslint-disable-line no-unused-vars
  const [queueLoading, setQueueLoading] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('finalExam'); // Default to Final Exam tab
  const [selectedTerm, setSelectedTerm] = useState(4); // Default to Term 4
  const [isLoadingResponses, setIsLoadingResponses] = useState(false);
  const [loadedResponsesForTabs, setLoadedResponsesForTabs] = useState(new Set()); // Track which tabs have loaded responses
  const [showArchivedResponses, setShowArchivedResponses] = useState(false); // Toggle for super_admin to view archived responses
  const [transformationMethods, setTransformationMethods] = useState({}); // Track transformation method selection per template {templateId: 'statistical' | 'traditional'}

  // 考试批次（Batch）状态：每个模板选中的批次（未选时默认currentBatchId）
  const [selectedBatchByTemplate, setSelectedBatchByTemplate] = useState({}); // {templateId: batchId | 'all'}
  const [batchModalTemplateId, setBatchModalTemplateId] = useState(null); // Manage Batches modal
  const [newBatchName, setNewBatchName] = useState('');
  const [renamingBatchId, setRenamingBatchId] = useState(null);
  const [renameBatchName, setRenameBatchName] = useState('');
  const [batchActionLoading, setBatchActionLoading] = useState(false);

  // Internal ranking modal state (full rankings; parent-facing PDF reports only show top 10)
  const [rankingModalTemplateId, setRankingModalTemplateId] = useState(null);
  const [rankingData, setRankingData] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingError, setRankingError] = useState(null);
  const [rankingGroupFilter, setRankingGroupFilter] = useState('all');

  // Campus state
  const [campuses, setCampuses] = useState([]);
  const [selectedCampusFilter, setSelectedCampusFilter] = useState('all');
  const [userCampusIds, setUserCampusIds] = useState([]);

  // Add auto-refresh interval ref
  const queueRefreshInterval = useRef(null);
  const notificationIdCounter = useRef(0);

  // Portal role
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalRoles = userGroups.filter(g => g.startsWith('portal_')).map(g => g.replace('portal_', ''));
  const highestRole = getHighestRole(portalRoles);

  // Legacy role checks (kept for fetchData compatibility)
  const isAdmin = userGroups.includes('site_admin');
  const isTeacher = userGroups.includes('teacher');
  const isSuperAdmin = userGroups.includes('super_admin');
  const isLocalhost = window.location.hostname.includes('localhost');

  // 凡是能"生成报告"的角色（含校区manager）都应能看到右侧报告队列/进度条，
  // 否则manager点了生成报告却看不到任何进度。删除/清空队列等破坏性操作仍仅限canManageReportQueue。
  const canViewReportQueue = isAdmin || canManageReportQueue(highestRole) || canGenerateTestReport(highestRole);

  // Determine if user has portal access
  const hasPortalAccess = !!highestRole;

  // API Gateway endpoint
  const apiBaseUrl = 'https://4vkpvv0re3.execute-api.ap-southeast-2.amazonaws.com/v1';

  // Load campuses on mount
  useEffect(() => {
    const loadCampuses = async () => {
      try {
        const result = await listCampuses();
        setCampuses(withoutTestDemoCampus(result));
      } catch (err) { console.error(err); }
    };
    if (auth.isAuthenticated) loadCampuses();
  }, [auth.isAuthenticated]);

  // Get user campus IDs from profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await getMyProfile();
        setUserCampusIds(profile?.campusIds || (profile?.campusId ? [profile.campusId] : []));
      } catch (err) { console.error(err); }
    };
    if (auth.isAuthenticated) loadProfile();
  }, [auth.isAuthenticated]);

  // Get campus name helper
  const getCampusName = (campusId) => {
    if (!campusId) return 'Unassigned';
    // 测试账号建在 Test Demo 下的模板：不显示校区名，也不要显示成 Unknown
    if (isTestDemoCampusId(campusId)) return '';
    return campuses.find(c => c.id === campusId)?.name || 'Unknown';
  };

  // ============ 考试批次（Batch）辅助函数 ============
  const getTemplateBatches = useCallback((templateId) => {
    const template = testTemplates.find(t => t.id === templateId);
    return template?.batches || [];
  }, [testTemplates]);

  // 当前模板选中的批次id：用户选择优先，否则用模板的currentBatchId，都没有则'all'
  const getSelectedBatchId = useCallback((templateId) => {
    const explicit = selectedBatchByTemplate[templateId];
    if (explicit) return explicit;
    const template = testTemplates.find(t => t.id === templateId);
    return template?.currentBatchId || 'all';
  }, [selectedBatchByTemplate, testTemplates]);

  const getBatchName = useCallback((templateId, batchId) => {
    if (!batchId) return null;
    const batch = getTemplateBatches(templateId).find(b => b.id === batchId);
    return batch ? batch.name : null;
  }, [getTemplateBatches]);

  // 批次操作后把返回的batches/currentBatchId合并回模板列表state
  const applyBatchResult = (templateId, result) => {
    setTestTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, batches: result.batches, currentBatchId: result.currentBatchId }
        : t
    ));
  };

  // Filter templates by campus and permission
  const filteredTemplates = useMemo(() => testTemplates.filter(template => {
    // First check permission
    if (!canViewTestTemplate(highestRole, template, userCampusIds)) return false;
    // Then apply campus filter dropdown
    if (selectedCampusFilter === 'all') return true;
    if (selectedCampusFilter === 'unassigned') return !template.campusId;
    return template.campusId === selectedCampusFilter;
  }), [testTemplates, highestRole, userCampusIds, selectedCampusFilter]);

  // 按模板ID分组测试响应，并在每个模板下按group进行二级分组，根据activeTab过滤
  const groupedResponses = useMemo(() => {
    return testResponses.reduce((groups, response) => {
      const templateId = response.testTemplateId;

      // 查找对应的模板
      const template = testTemplates.find(t => t.id === templateId);
      const templateType = template?.templateData?.templateType || 'mockTest';

      // 根据activeTab过滤响应
      if (templateType !== activeTab) {
        return groups;
      }

      // Check if this template passes the campus/permission filter
      if (!filteredTemplates.find(t => t.id === templateId)) {
        return groups;
      }

      // 按选中的考试批次过滤（'all'显示全部；未分配批次的response始终显示，避免数据被隐藏）
      const selectedBatch = getSelectedBatchId(templateId);
      if (selectedBatch !== 'all' && response.batchId && response.batchId !== selectedBatch) {
        return groups;
      }

      if (!groups[templateId]) {
        groups[templateId] = {};
      }

      // 从testResponse中获取group，如果没有则使用"No Group"
      const responseGroup = response.responseGroup || response.testResponse?.group || response.group || 'No Group';

      if (!groups[templateId][responseGroup]) {
        groups[templateId][responseGroup] = [];
      }

      groups[templateId][responseGroup].push(response);
      return groups;
    }, {});
  }, [testResponses, testTemplates, activeTab, filteredTemplates, getSelectedBatchId]);

  // 初始化时只展开模板类型分组，不展开responses
  useEffect(() => {
    const templateTypes = [...new Set(testTemplates.map(t => t.templateData?.templateType || 'mockTest'))];

    // 只展开模板类型分组
    if (templateTypes.length > 0) {
      setExpandedGroups(new Set(templateTypes));
    }
  }, [testTemplates]);

  // Notification functions
  const showNotification = (message, type = 'info') => {
    const id = ++notificationIdCounter.current;
    const notification = {
      id,
      message,
      type,
      timestamp: Date.now()
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Get display name with fallback logic
  const getDisplayName = useCallback(() => {
    const profile = auth.user?.profile;
    return profile?.name || profile?.email || 'User';
  }, [auth.user?.profile]);

  // Debug: Log available Cognito user fields
  useEffect(() => {
    if (auth.user) {
      console.log('=== COGNITO USER DEBUG INFO (Portal TestManagement) ===');
      console.log('Full auth.user object:', auth.user);
      console.log('auth.user.profile:', auth.user.profile);
      console.log('Available profile fields:', Object.keys(auth.user.profile || {}));
      console.log('Profile name field:', auth.user.profile?.name);
      console.log('Profile email field:', auth.user.profile?.email);
      console.log('Current display name result:', getDisplayName());
      console.log('Portal roles:', portalRoles);
      console.log('Highest role:', highestRole);
      console.log('================================');
    }
  }, [auth.user, getDisplayName, portalRoles, highestRole]);

  // Function to refresh test responses for a specific template
  const refreshTestResponsesForTemplate = useCallback(async (templateId) => {
    try {
      console.log(`Refreshing test responses for template: ${templateId}`);
      const responseData = await testDataAPI.getTestDataByTemplateId(templateId, auth.user?.id_token, showArchivedResponses);
      if (Array.isArray(responseData)) {
        // Update test responses by replacing responses for this template
        setTestResponses(prev => {
          // Remove existing responses for this template
          const filtered = prev.filter(response => response.testTemplateId !== templateId);
          // Combine with new responses and deduplicate based on ID
          const combined = [...filtered, ...responseData];
          const uniqueResponses = combined.filter((response, index, self) =>
            index === self.findIndex(r => r.id === response.id)
          );
          return uniqueResponses;
        });
      }
    } catch (error) {
      console.warn(`Failed to refresh responses for template ${templateId}:`, error);
    }
  }, [auth.user?.id_token, showArchivedResponses]);


  // Report Queue API functions
  const fetchReportQueue = useCallback(async () => {
    if (!canViewReportQueue || !auth.user?.id_token) return;

    setQueueLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/test-report-queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user.id_token}`,
          'X-Amz-Date': new Date().toISOString(),
          'X-Api-Key': '', // Add if you have an API key
        },
        body: JSON.stringify({ operation: 'read' })
      });
        if (response.ok) {
        const data = await response.json();
        // Sort by created_at in descending order (newest first)
        const sortedData = Array.isArray(data)
          ? data
              .filter(item => item && item.created_at) // Filter out invalid items
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          : [];

        // Check for status changes and refresh test responses if needed
        setPreviousQueueData(prevQueueData => {
          if (prevQueueData.length > 0) {
            const templatesWithStatusChange = new Set();

            sortedData.forEach(currentItem => {
              const previousItem = prevQueueData.find(prev => prev.id === currentItem.id);
              if (previousItem &&
                  previousItem.queueStatus !== currentItem.queueStatus &&
                  currentItem.queueStatus === 'completed' &&
                  currentItem.templateId) {
                console.log(`Queue item completed for template ${currentItem.templateId}, refreshing responses`);
                templatesWithStatusChange.add(currentItem.templateId);
              }
            });

            // Refresh test responses for templates with completed queue items
            templatesWithStatusChange.forEach(templateId => {
              refreshTestResponsesForTemplate(templateId);

              // Show notification for successful completion
              const completedItem = sortedData.find(item => item.templateId === templateId && item.queueStatus === 'completed');
              if (completedItem && completedItem.successfulReports > 0) {
                showNotification(
                  `Reports completed for "${completedItem.templateName}"${completedItem.targetGroup ? ` (${completedItem.targetGroup})` : ''}! Download links are now available.`,
                  'success'
                );
              } else if (completedItem && completedItem.failedReports > 0) {
                showNotification(
                  `Report generation completed for "${completedItem.templateName}" with ${completedItem.failedReports} failures. Check the queue for details.`,
                  'warning'
                );
              }
            });
          }

          return sortedData;
        });

        setReportQueue(sortedData);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch report queue:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching report queue:', error);
    } finally {
      setQueueLoading(false);
    }
  }, [canViewReportQueue, auth.user?.id_token, apiBaseUrl, refreshTestResponsesForTemplate]);

  // Rate limiter utility function wrapped in useCallback
  const rateLimitedPromises = useCallback(async (promiseFunctions, maxConcurrent = 3, retryDelay = 500) => {
    const results = [];
    let index = 0;

    const executeWithRetry = async (promiseFunc) => {
      try {
        return await promiseFunc();
      } catch (error) {
        // Retry logic with delay
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        try {
          return await promiseFunc();
        } catch (retryError) {
          return { error: retryError };
        }
      }
    };

    // Process promises in batches of maxConcurrent
    while (index < promiseFunctions.length) {
      const batch = promiseFunctions.slice(index, index + maxConcurrent);
      const batchPromises = batch.map(promiseFunc => executeWithRetry(promiseFunc));

      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults);

      index += maxConcurrent;
    }

    return results;
  }, []);

  // 获取数据
  useEffect(() => {
    // 只有在已认证的情况下才获取数据
    if (!auth.isAuthenticated) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 获取 TestTemplate 数据 - check both legacy and portal roles
        if (isAdmin || isTeacher || hasPortalAccess) {
          const templateData = await testTemplateAPI.getAllTemplatesSummary(auth.user?.id_token);
          const templates = Array.isArray(templateData) ? templateData : [];
          setTestTemplates(templates);

          // 获取 Report Queue 数据
          if (canViewReportQueue) {
            await fetchReportQueue();
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [auth.isAuthenticated, auth.user?.id_token, isAdmin, isTeacher, hasPortalAccess, highestRole, canViewReportQueue, fetchReportQueue]);

  // Clear loaded responses when archived toggle changes
  useEffect(() => {
    if (isSuperAdmin || isAdmin || canViewArchivedResponses(highestRole)) {
      setLoadedResponsesForTabs(new Set()); // Clear loaded tabs to force refetch
    }
  }, [showArchivedResponses, isSuperAdmin, isAdmin, highestRole]);

  // Load test responses automatically when templates are available or tab/archived changes
  useEffect(() => {
    const loadTestResponses = async () => {
      const cacheKey = `${activeTab}-${showArchivedResponses}`;
      if (loadedResponsesForTabs.has(cacheKey) || !auth.user?.id_token || (!isAdmin && !isTeacher && !hasPortalAccess)) {
        return;
      }

      // Wait for templates to finish loading
      if (isLoading || testTemplates.length === 0) {
        return;
      }

      // Filter templates for the current active tab only
      const templatesForCurrentTab = testTemplates.filter(template => {
        const templateType = template.templateData?.templateType || 'mockTest';
        return templateType === activeTab;
      });

      if (templatesForCurrentTab.length === 0) {
        setLoadedResponsesForTabs(prev => new Set([...prev, cacheKey]));
        return;
      }

      setIsLoadingResponses(true);
      // Clear responses for fresh load
      setTestResponses([]);
      try {
        const seenIds = new Set();

        const requestFunctions = templatesForCurrentTab.map(template =>
          async () => {
            try {
              const responseData = await testDataAPI.getTestDataByTemplateId(template.id, auth.user?.id_token, showArchivedResponses, true);
              if (Array.isArray(responseData) && responseData.length > 0) {
                // Deduplicate and immediately append to state
                const newItems = responseData.filter(r => !seenIds.has(r.id));
                newItems.forEach(r => seenIds.add(r.id));
                if (newItems.length > 0) {
                  setTestResponses(prev => [...prev, ...newItems]);
                }
              }
              return { templateId: template.id, success: true };
            } catch (error) {
              console.warn(`Failed to fetch responses for template ${template.id}:`, error);
              return { templateId: template.id, error };
            }
          }
        );

        await rateLimitedPromises(requestFunctions, 3, 500);

        setLoadedResponsesForTabs(prev => new Set([...prev, cacheKey]));
      } catch (error) {
        console.error('Error loading test responses:', error);
      } finally {
        setIsLoadingResponses(false);
      }
    };

    loadTestResponses();
  }, [loadedResponsesForTabs, auth.user?.id_token, isAdmin, isTeacher, hasPortalAccess, testTemplates, rateLimitedPromises, activeTab, showArchivedResponses, isLoading]);

  // Auto-refresh effect for queue - runs regardless of panel state
  useEffect(() => {
    if (canViewReportQueue && auth.user?.id_token) {
      // Fetch immediately
      fetchReportQueue();

      // Set up interval for auto-refresh every 10 seconds
      const interval = setInterval(() => {
        fetchReportQueue();
      }, 10000);

      queueRefreshInterval.current = interval;

      // Cleanup interval when component unmounts or auth changes
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear interval when not admin or not authenticated
      if (queueRefreshInterval.current) {
        clearInterval(queueRefreshInterval.current);
        queueRefreshInterval.current = null;
      }
    }
  }, [canViewReportQueue, auth.user?.id_token, fetchReportQueue]);

  // 跳转到TestTemplate页面
  const editTemplate = (templateId) => {
    navigate(`/portal/test-template?id=${templateId}`);
  };

  // 创建新的TestTemplate
  const createNewTemplate = () => {
    navigate(`/portal/test-template?type=${activeTab}`);
  };

  // 克隆模板
  const cloneTemplate = (template) => {
    // 简单地传递模板ID和克隆标识
    navigate(`/portal/test-template?cloneFrom=${template.id}`);
  };

  // 创建新的TestResponse
  const createNewTestResponse = () => {
    navigate(`/portal/test-response?templateType=${activeTab}`);
  };

  // 跳转到TestResponse页面
  const editTestResponse = (responseId) => {
    navigate(`/portal/test-response?id=${responseId}`);
  };

  // 删除TestTemplate
  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      await testTemplateAPI.deleteTemplate(templateId, auth.user?.id_token);
      setTestTemplates(testTemplates.filter(template => template.id !== templateId));
      console.log('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 删除TestResponse
  const deleteTestResponse = async (responseId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete test response for "${studentName}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      await testDataAPI.deleteTestData(responseId, auth.user?.id_token);
      setTestResponses(testResponses.filter(response => response.id !== responseId));
      console.log('Test response deleted successfully');
    } catch (error) {
      console.error('Error deleting test response:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 切换分组展开状态
  const toggleGroup = (templateId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  // 归档/取消归档模板下所有响应
  const archiveTemplateResponses = async (templateId, isArchived) => {
    const templateName = getTemplateName(templateId);
    const action = isArchived ? 'archive' : 'unarchive';
    if (!window.confirm(`Are you sure you want to ${action} all responses for "${templateName}"?`)) {
      return;
    }
    try {
      const result = await testDataAPI.archiveByTemplate(templateId, isArchived, auth.user?.id_token);
      alert(`Successfully ${action}d ${result.updatedCount} response(s).`);
      // Clear cache and reload responses for current tab
      setLoadedResponsesForTabs(new Set());
      setTestResponses([]);
    } catch (error) {
      console.error(`Error ${action} responses:`, error);
      alert(`Failed to ${action} responses: ${error.message}`);
    }
  };

  // ============ 批次管理操作（Manage Batches modal） ============
  const openBatchModal = (templateId) => {
    setBatchModalTemplateId(templateId);
    setNewBatchName('');
    setRenamingBatchId(null);
    setRenameBatchName('');
  };

  const closeBatchModal = () => {
    setBatchModalTemplateId(null);
    setNewBatchName('');
    setRenamingBatchId(null);
    setRenameBatchName('');
  };

  // 已加载到前端的response中属于某批次的数量（提示用；未勾选Show Archived时不含归档数据）
  const getBatchResponseCount = (templateId, batchId) => {
    return testResponses.filter(r => r.testTemplateId === templateId && r.batchId === batchId).length;
  };

  const handleCreateBatch = async () => {
    const name = newBatchName.trim();
    if (!name) {
      alert('Please enter a batch name.');
      return;
    }
    setBatchActionLoading(true);
    try {
      const result = await testTemplateAPI.createBatch(batchModalTemplateId, name, auth.user?.id_token, true);
      applyBatchResult(batchModalTemplateId, result);
      setNewBatchName('');
      showNotification(`Batch "${name}" created and set as current.`, 'success');
    } catch (error) {
      alert(`Failed to create batch: ${error.message}`);
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleSetCurrentBatch = async (batchId) => {
    setBatchActionLoading(true);
    try {
      const result = await testTemplateAPI.updateBatch(batchModalTemplateId, batchId, auth.user?.id_token, { setCurrent: true });
      applyBatchResult(batchModalTemplateId, result);
    } catch (error) {
      alert(`Failed to set current batch: ${error.message}`);
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleRenameBatch = async () => {
    const name = renameBatchName.trim();
    if (!name) {
      alert('Please enter a new batch name.');
      return;
    }
    setBatchActionLoading(true);
    try {
      const result = await testTemplateAPI.updateBatch(batchModalTemplateId, renamingBatchId, auth.user?.id_token, { name });
      applyBatchResult(batchModalTemplateId, result);
      setRenamingBatchId(null);
      setRenameBatchName('');
    } catch (error) {
      alert(`Failed to rename batch: ${error.message}`);
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId, batchName) => {
    if (!window.confirm(`Delete batch "${batchName}"? Only empty batches (no responses) can be deleted.`)) {
      return;
    }
    setBatchActionLoading(true);
    try {
      const result = await testTemplateAPI.deleteBatch(batchModalTemplateId, batchId, auth.user?.id_token);
      applyBatchResult(batchModalTemplateId, result);
      // 若被删批次正被选中，重置选择
      setSelectedBatchByTemplate(prev => {
        if (prev[batchModalTemplateId] === batchId) {
          const next = { ...prev };
          delete next[batchModalTemplateId];
          return next;
        }
        return prev;
      });
    } catch (error) {
      alert(`Failed to delete batch: ${error.message}`);
    } finally {
      setBatchActionLoading(false);
    }
  };

  // 生成特定group的报告
  const generateGroupResponsesReport = (templateId, groupName) => {
    const key = `${templateId}-${groupName}`;
    // disable this button for 5 seconds
    setDisabledReportButtons(prev => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    // 按选中的考试批次生成（'all'为旧行为：当期未归档学生）
    const selectedBatch = getSelectedBatchId(templateId);
    const batchId = selectedBatch !== 'all' ? selectedBatch : null;
    const batchLabel = batchId ? ` (batch: ${getBatchName(templateId, batchId) || batchId})` : '';

    // Show alert immediately after sending the request
    alert(`Report generation request sent for group "${groupName}"${batchLabel}, please check Report queue for progress.`);

    // 自动展开右侧报告队列面板，让用户立刻看到生成进度
    setShowQueue(true);
    fetchReportQueue();

    // Fire-and-forget report API call
    testReportAPI.generateAllResponsesReport(templateId, auth.user?.id_token, groupName, false, null, batchId)
      .catch((error) => {
        console.log("Error generating group report or timeout:", error);
      });

    // re-enable the button after 5 seconds
    setTimeout(() => {
      setDisabledReportButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }, 5000);
  };

  // 生成特定group的数据导出（仅限localhost）
  const generateDataOnlyForGroup = async (templateId, groupName) => {
    try {
      const result = await testReportAPI.generateDataOnly(templateId, auth.user?.id_token, groupName);
      console.log('Data export result:', result);

      if (result.url) {
        // 自动下载JSON文件
        const link = document.createElement('a');
        link.href = result.url;
        link.download = result.filename || `data_export_${groupName}_${Date.now()}.json`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log(`Data export completed for group "${groupName}". ${result.data_count} records exported.`);
      } else {
        console.log(`Data export completed for group "${groupName}", but no download URL was provided.`);
      }
    } catch (error) {
      console.error('Error generating data export:', error);
    }
  };

  // 生成整个模板的数据导出（仅限localhost）
  const generateDataOnlyForTemplate = async (templateId) => {
    try {
      const result = await testReportAPI.generateDataOnly(templateId, auth.user?.id_token);
      console.log('Template data export result:', result);

      if (result.url) {
        // 自动下载JSON文件
        const link = document.createElement('a');
        link.href = result.url;
        link.download = result.filename || `data_export_template_${templateId}_${Date.now()}.json`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log(`Template data export completed. ${result.data_count} records exported.`);
      } else {
        console.log(`Template data export completed, but no download URL was provided.`);
      }
    } catch (error) {
      console.error('Error generating template data export:', error);
    }
  };

  // 生成整个模板的报告（所有groups）
  const generateReportForTemplate = (templateId, transformationMethod = null) => {
    const key = `template-${templateId}`;
    // disable this button for 5 seconds
    setDisabledReportButtons(prev => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    // 按选中的考试批次生成（'all'为旧行为：当期未归档学生）
    const selectedBatch = getSelectedBatchId(templateId);
    const batchId = selectedBatch !== 'all' ? selectedBatch : null;
    const batchLabel = batchId ? ` Batch: ${getBatchName(templateId, batchId) || batchId}.` : '';

    // Show alert immediately after sending the request
    alert(`Report generation request sent for all groups in this template.${batchLabel} Please check the Report Queue for progress.`);

    // 自动展开右侧报告队列面板，让用户立刻看到生成进度
    setShowQueue(true);
    fetchReportQueue();

    // Fire-and-forget report API call with includeAllGroups parameter and transformation method
    testReportAPI.generateAllResponsesReport(templateId, auth.user?.id_token, null, true, transformationMethod, batchId)
      .catch((error) => {
        console.log("Error generating template report or timeout:", error);
      });

    // re-enable the button after 5 seconds
    setTimeout(() => {
      setDisabledReportButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }, 5000);
  };

  // 打开内部排名查看modal并加载完整排名数据
  const openRankingModal = async (templateId) => {
    setRankingModalTemplateId(templateId);
    setRankingData(null);
    setRankingError(null);
    setRankingGroupFilter('all');
    setRankingLoading(true);
    try {
      // 按选中的考试批次查排名（'all'为旧行为：当期未归档学生）
      const selectedBatch = getSelectedBatchId(templateId);
      const batchId = selectedBatch !== 'all' ? selectedBatch : null;
      const result = await testReportAPI.getRankings(templateId, auth.user?.id_token, batchId);
      setRankingData(result);
    } catch (error) {
      console.error('Error loading rankings:', error);
      setRankingError('Failed to load rankings. Please try again.');
    } finally {
      setRankingLoading(false);
    }
  };

  const closeRankingModal = () => {
    setRankingModalTemplateId(null);
    setRankingData(null);
    setRankingError(null);
  };

  // 下载PDF报告
  const downloadPdfReport = (pdfUrl, studentName, reportType = '') => {
    try {
      // 添加时间戳参数以绕过CloudFront缓存
      const timestamp = Date.now();
      const urlWithCacheBuster = pdfUrl.includes('?')
        ? `${pdfUrl}&t=${timestamp}`
        : `${pdfUrl}?t=${timestamp}`;

      // 直接创建下载链接，避免CORS问题
      const link = document.createElement('a');
      link.href = urlWithCacheBuster;
      link.download = `${studentName}_report${reportType ? `_${reportType}` : ''}.pdf`;
      link.target = '_blank'; // 在新标签页打开，以防下载失败时用户可以手动保存
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF report. Please try again.');
    }
  };

  // 下载组级别的zip报告
  const downloadGroupZipReport = (zipUrl, templateName, groupName, reportType = '') => {
    try {
      // 添加时间戳参数以绕过CloudFront缓存
      const timestamp = Date.now();
      const urlWithCacheBuster = zipUrl.includes('?')
        ? `${zipUrl}&t=${timestamp}`
        : `${zipUrl}?t=${timestamp}`;

      const link = document.createElement('a');
      link.href = urlWithCacheBuster;
      link.download = `${templateName}_${groupName}_reports${reportType ? `_${reportType}` : ''}.zip`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading zip report:', error);
      alert('Error downloading zip report. Please try again.');
    }
  };

  // 解析模板的zipUrl并获取指定组的下载链接 (支持常规组和all-groups结构)
  const getGroupZipUrls = (template, groupName) => {
    try {
      if (!template?.zipUrl) return {
        regularZipUrl: null,
        allGroupsZipUrl: null
      };

      const zipData = JSON.parse(template.zipUrl);

      // Get regular group ZIP URL
      const regularGroupData = zipData[groupName] || {};
      const regularZipUrl = regularGroupData.pdfUrl || null;

      // Get all-groups individual ZIP URL
      const individualGroups = zipData.individual_groups || {};
      const allGroupsData = individualGroups[groupName] || {};
      const allGroupsZipUrl = allGroupsData.pdfUrl || null;

      return {
        regularZipUrl,
        allGroupsZipUrl
      };
    } catch (error) {
      console.error('Error parsing zipUrl:', error);
      return {
        regularZipUrl: null,
        allGroupsZipUrl: null
      };
    }
  };

  // 获取模板级别的所有组ZIP下载链接 (支持新的all-groups结构)
  const getTemplateZipUrls = (template) => {
    try {
      if (!template?.zipUrl) return {
        hasRegularZips: false,
        hasMasterZip: false,
        masterZipUrl: null,
        regularGroups: {},
        allGroupsIndividual: {}
      };

      const zipData = JSON.parse(template.zipUrl);

      // Check for new all-groups structure
      const masterZip = zipData.master_zip || {};
      const individualGroups = zipData.individual_groups || {};

      // Check for regular group ZIPs (backward compatibility)
      const regularGroups = {};
      Object.keys(zipData).forEach(key => {
        if (key !== 'master_zip' && key !== 'individual_groups' && zipData[key]?.pdfUrl) {
          regularGroups[key] = zipData[key];
        }
      });

      return {
        hasRegularZips: Object.keys(regularGroups).length > 0,
        hasMasterZip: !!masterZip.pdfUrl,
        masterZipUrl: masterZip.pdfUrl || null,
        regularGroups,
        allGroupsIndividual: individualGroups
      };
    } catch (error) {
      console.error('Error parsing template zipUrl:', error);
      return {
        hasRegularZips: false,
        hasMasterZip: false,
        masterZipUrl: null,
        regularGroups: {},
        allGroupsIndividual: {}
      };
    }
  };

  // 下载所有组报告的ZIP文件 (使用新的master ZIP结构)
  const downloadMasterZipReport = (template) => {
    try {
      const templateZipUrls = getTemplateZipUrls(template);

      if (!templateZipUrls.hasMasterZip || !templateZipUrls.masterZipUrl) {
        alert('No master all-groups report available for download.');
        return;
      }

      // Download the master organized ZIP file
      downloadGroupZipReport(templateZipUrls.masterZipUrl, template.templateData?.examName || template.templateName, 'AllGroups_Organized', '');

    } catch (error) {
      console.error('Error downloading master ZIP:', error);
      alert('Error downloading master ZIP file. Please try again.');
    }
  };

  // 下载特定组的all-groups ZIP文件
  const downloadGroupAllGroupsZip = (template, groupName) => {
    try {
      const groupZipUrls = getGroupZipUrls(template, groupName);

      if (!groupZipUrls.allGroupsZipUrl) {
        alert(`No all-groups ZIP available for group ${groupName}.`);
        return;
      }

      // Download the group's all-groups ZIP file
      downloadGroupZipReport(groupZipUrls.allGroupsZipUrl, template.templateData?.examName || template.templateName, `${groupName}_AllGroups`, '');

    } catch (error) {
      console.error('Error downloading group all-groups ZIP:', error);
      alert(`Error downloading all-groups ZIP for group ${groupName}. Please try again.`);
    }
  };

  // 下载学生的所有组报告
  const downloadAllGroupsReport = (response, withCover = true) => {
    try {
      const url = withCover ? response.pdfUrlGroup : response.pdfUrlNoCoverGroup;
      if (!url) {
        alert(`All-groups report ${withCover ? 'with cover' : 'without cover'} not available for this student.`);
        return;
      }

      // 添加时间戳参数以绕过CloudFront缓存
      const timestamp = Date.now();
      const urlWithCacheBuster = url.includes('?')
        ? `${url}&t=${timestamp}`
        : `${url}?t=${timestamp}`;

      // 直接创建下载链接
      const link = document.createElement('a');
      link.href = urlWithCacheBuster;
      link.download = `${response.studentName}_all_groups_report${withCover ? '' : '_no_cover'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error downloading all-groups report:', error);
      alert('Error downloading all-groups report. Please try again.');
    }
  };

  // 按模板类型分组模板，根据activeTab和selectedTerm过滤
  // 按模板名称进行字典序排序
  const sortedTemplates = [...filteredTemplates].sort((a, b) => a.templateName.localeCompare(b.templateName));
  const groupedTemplates = sortedTemplates.reduce((groups, template) => {
    // 从templateData中获取templateType，如果没有则默认为mockTest
    const templateType = template.templateData?.templateType || 'mockTest';

    // 根据activeTab过滤模板
    if (templateType !== activeTab) {
      return groups;
    }

    // 根据selectedTerm过滤模板
    const templateTerm = template.term;
    if (selectedTerm === 'other') {
      // 'other' 显示没有term的模板（null/undefined）
      if (templateTerm !== null && templateTerm !== undefined) {
        return groups;
      }
    } else {
      // 数字term时，显示匹配的模板
      if (templateTerm !== selectedTerm) {
        return groups;
      }
    }

    if (!groups[templateType]) {
      groups[templateType] = [];
    }
    groups[templateType].push(template);
    return groups;
  }, {});

  // 获取模板类型的显示名称
  const getTemplateTypeDisplayName = (templateType) => {
    switch (templateType) {
      case 'mockTest':
        return 'Mock Test Templates';
      case 'finalExam':
        return 'Final Exam Templates';
      default:
        return `${templateType} Templates`;
    }
  };

  // 获取模板名称
  const getTemplateName = (templateId) => {
    const template = testTemplates.find(t => t.id === templateId);
    return template ? template.templateName : `Unknown Template (${templateId})`;
  };

  // 获取当前tab的展开状态
  const isCurrentTabResponsesExpanded = expandedResponsesByTab[activeTab] || false;

  const deleteQueueItem = async (queueId) => {
    if ((!isAdmin && !canManageReportQueue(highestRole)) || !auth.user?.id_token) return;

    if (!window.confirm('Are you sure you want to delete this queue item?')) {
      return;
    }
      try {
      const response = await fetch(`${apiBaseUrl}/test-report-queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user.id_token}`,
          'X-Amz-Date': new Date().toISOString(),
          'X-Api-Key': '', // Add if you have an API key
        },
        body: JSON.stringify({
          operation: 'delete',
          data: { id: queueId }
        })
      });
        if (response.ok) {
        setReportQueue(prev => prev.filter(item => item.id !== queueId));
        alert('Queue item deleted successfully!');
      } else {
        const errorText = await response.text();
        console.error('Failed to delete queue item:', response.status, errorText);
        alert(`Failed to delete queue item: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting queue item:', error);
      alert('Error deleting queue item. Please try again.');
    }
  };

  const deleteAllQueueItems = async () => {
    if ((!isAdmin && !canManageReportQueue(highestRole)) || !auth.user?.id_token || reportQueue.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ALL ${reportQueue.length} queue items? This action cannot be undone.`)) {
      return;
    }

    setQueueLoading(true);
    try {
      const deletePromises = reportQueue.map(item =>
        fetch(`${apiBaseUrl}/test-report-queue`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.user.id_token}`,
            'X-Amz-Date': new Date().toISOString(),
            'X-Api-Key': '',
          },
          body: JSON.stringify({
            operation: 'delete',
            data: { id: item.id }
          })
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.ok).length;
      const failed = results.length - successful;

      if (successful > 0) {
        setReportQueue([]);
        showNotification(`Successfully deleted ${successful} queue items${failed > 0 ? `, ${failed} failed` : ''}`, 'success');
      } else {
        showNotification('Failed to delete queue items', 'warning');
      }
    } catch (error) {
      console.error('Error deleting all queue items:', error);
      showNotification('Error deleting queue items', 'warning');
    } finally {
      setQueueLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'processing': return '#007bff';
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <T.Page>
      {/* Notification Snackbar Container */}
      {notifications.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            maxWidth: '400px'
          }}
        >
          {notifications.map((notification) => (
            <div
              key={notification.id}
              style={{
                background: notification.type === 'success'
                  ? 'linear-gradient(135deg, #28a745, #20c997)'
                  : notification.type === 'warning'
                  ? 'linear-gradient(135deg, #ffc107, #fd7e14)'
                  : 'linear-gradient(135deg, #007bff, #6610f2)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                fontSize: '14px',
                lineHeight: '1.4',
                animation: 'slideInFromRight 0.3s ease-out',
                cursor: 'pointer',
                border: 'none',
                fontWeight: '500',
                wordBreak: 'break-word'
              }}
              onClick={() => removeNotification(notification.id)}
            >
              {notification.message}
              <div style={{
                fontSize: '11px',
                opacity: 0.8,
                marginTop: '4px',
                textAlign: 'right'
              }}>
                Click to dismiss
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>Loading...</p>
        </div>
      )}

      {/* Campus Filter — hidden until templates actually get assigned to a campus (all campusId
          values are currently null); logic/state kept intact, just flip this flag to bring it back */}
      {SHOW_CAMPUS_FILTER && (
        <T.FilterRow style={{ marginBottom: '16px', gap: '12px' }}>
          <label style={{ fontWeight: 'bold' }}>Campus:</label>
          <ChipSelect
            size="sm"
            aria-label="Filter templates by campus"
            value={selectedCampusFilter}
            onChange={setSelectedCampusFilter}
            options={[
              { value: 'all', label: 'All Campuses' },
              ...(['developer', 'system_admin', 'director', 'manager'].includes(highestRole)
                ? [{ value: 'unassigned', label: 'Unassigned (Legacy)' }]
                : []),
              ...campuses.map(c => ({ value: c.id, label: c.name })),
            ]}
          />
        </T.FilterRow>
      )}

      {/* Tab Navigation */}
      {(isAdmin || isTeacher || hasPortalAccess) && (
        <S.Section>
          <T.TabBar>
            <button
              onClick={() => setActiveTab('finalExam')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'finalExam' ? '#007bff' : 'transparent',
                color: activeTab === 'finalExam' ? 'white' : '#007bff',
                borderBottom: activeTab === 'finalExam' ? '3px solid #007bff' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease'
              }}
            >
              Final Exam
            </button>
            <button
              onClick={() => setActiveTab('mockTest')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'mockTest' ? '#007bff' : 'transparent',
                color: activeTab === 'mockTest' ? 'white' : '#007bff',
                borderBottom: activeTab === 'mockTest' ? '3px solid #007bff' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease'
              }}
            >
              Mock Test
            </button>
          </T.TabBar>
        </S.Section>
      )}

      {/* TestTemplate 部分 */}
      {(isAdmin || isTeacher || hasPortalAccess) && (
        <S.Section>
          <T.SectionHeader
            style={{ cursor: 'pointer' }}
            onClick={() => setExpandedTemplates(!expandedTemplates)}
          >
            <h2 style={{ margin: 0 }}>{getTemplateTypeDisplayName(activeTab)}</h2>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {expandedTemplates ? '\u2212' : '+'}
            </div>
          </T.SectionHeader>
          {expandedTemplates && (
            <>
              {/* Term Filter */}
              <T.FilterRow style={{ marginBottom: '15px' }}>
                <span style={{ fontWeight: '600' }}>Term:</span>
                {[1, 2, 3, 4, 'other'].map(term => (
                  <button
                    key={term}
                    onClick={() => setSelectedTerm(term)}
                    style={{
                      padding: '6px 14px',
                      border: selectedTerm === term ? '2px solid #007bff' : '1px solid #dee2e6',
                      background: selectedTerm === term ? '#007bff' : 'white',
                      color: selectedTerm === term ? 'white' : '#495057',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: selectedTerm === term ? '600' : '400',
                      fontSize: '14px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {term === 'other' ? 'Other' : `Term ${term}`}
                  </button>
                ))}
              </T.FilterRow>

              {canCreateTestTemplate(highestRole) && (
                <S.FormContainer>
                  <S.Button onClick={createNewTemplate} disabled={isLoading}>
                    Create New Template
                  </S.Button>
                </S.FormContainer>
              )}

              {isLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                  Loading templates...
                </div>
              ) : Object.keys(groupedTemplates).length > 0 ? (
                <div>
                  {Object.entries(groupedTemplates).map(([templateType, templates]) => (
                    <S.ItemList key={templateType}>
                      {templates.map((template) => (
                        <T.ListRow key={template.id}>
                          <T.RowMeta>
                            <S.ItemText>{template.templateName}</S.ItemText>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                              Created: {new Date(template.created_at).toLocaleDateString()}
                              {template.templateData?.examDate && (
                                <span> | Exam Date: {new Date(template.templateData.examDate).toLocaleDateString()}</span>
                              )}
                              <span> | </span>
                              <span style={{ fontSize: '12px', color: '#888' }}>{getCampusName(template.campusId)}</span>
                            </div>
                          </T.RowMeta>
                          <T.ActionRow style={{ gap: '8px' }}>
                            <S.Button
                              onClick={() => editTemplate(template.id)}
                              style={{ padding: '6px 12px', fontSize: '14px' }}
                              disabled={isLoading}
                            >
                              {canEditTestTemplate(highestRole, template, userCampusIds) ? 'Edit' : 'View'}
                            </S.Button>
                            {canCreateTestTemplate(highestRole) && (
                              <S.Button
                                onClick={() => cloneTemplate(template)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '14px',
                                  background: '#28a745',
                                  color: 'white'
                                }}
                                disabled={isLoading}
                              >
                                Clone
                              </S.Button>
                            )}
                            {canDeleteTestTemplate(highestRole) && (
                              <S.Button
                                onClick={() => deleteTemplate(template.id, template.templateName)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '14px',
                                  background: '#dc3545',
                                  color: 'white'
                                }}
                                disabled={isLoading}
                              >
                                Delete
                              </S.Button>
                            )}
                          </T.ActionRow>
                        </T.ListRow>
                      ))}
                    </S.ItemList>
                  ))}
                </div>
              ) : (
                <S.EmptyState>
                  <h3>No {getTemplateTypeDisplayName(activeTab)} Yet</h3>
                  <p>Create your first {activeTab === 'finalExam' ? 'final exam' : 'mock test'} template to get started.</p>
                  {canCreateTestTemplate(highestRole) && (
                    <S.Button onClick={createNewTemplate} style={{ marginTop: '10px' }}>
                      Create Your First {activeTab === 'finalExam' ? 'Final Exam' : 'Mock Test'} Template
                    </S.Button>
                  )}
                </S.EmptyState>
              )}
            </>
          )}
        </S.Section>
      )}

      {/* Test Responses 部分 */}
      {(isAdmin || isTeacher || hasPortalAccess) && (
        <S.Section>
          <T.SectionHeader>
            <div
              style={{
                cursor: 'pointer',
                flex: '1 1 180px',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center'
              }}
              onClick={() => setExpandedResponsesByTab(prev => ({
                ...prev,
                [activeTab]: !prev[activeTab]
              }))}
            >
              <h2 style={{ margin: 0 }}>{getTemplateTypeDisplayName(activeTab)} - Test Responses</h2>
            </div>

            {/* Archive Toggle */}
            {(isSuperAdmin || isAdmin || canViewArchivedResponses(highestRole)) && isCurrentTabResponsesExpanded && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <input
                    type="checkbox"
                    checked={showArchivedResponses}
                    onChange={(e) => setShowArchivedResponses(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Show Archived
                </label>
              </div>
            )}

            <div
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: '0 8px'
              }}
              onClick={() => setExpandedResponsesByTab(prev => ({
                ...prev,
                [activeTab]: !prev[activeTab]
              }))}
            >
              {isCurrentTabResponsesExpanded ? '\u2212' : '+'}
            </div>
          </T.SectionHeader>
          {isCurrentTabResponsesExpanded && canEditTestResponse(highestRole) && (
            <S.FormContainer>
              <S.Button onClick={createNewTestResponse}>
                Create New Test Response
              </S.Button>
            </S.FormContainer>
          )}
          {isCurrentTabResponsesExpanded && (
            <>
              {Object.keys(groupedResponses).length > 0 ? (
                <div>
                  {Object.entries(groupedResponses)
                .sort(([templateIdA], [templateIdB]) => {
                  const templateNameA = getTemplateName(templateIdA);
                  const templateNameB = getTemplateName(templateIdB);
                  return templateNameA.localeCompare(templateNameB);
                })
                .map(([templateId, groupsData]) => {
                // 计算该模板下的总response数量
                const totalResponses = Object.values(groupsData).reduce((total, groupResponses) => total + groupResponses.length, 0);

                return (
                  <div key={templateId} style={{ marginBottom: '20px' }}>
                    {/* 模板级别分组标题 */}
                    <div
                      style={{
                        background: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        marginBottom: expandedGroups.has(templateId) ? '10px' : '0'
                      }}
                    >
                      {/* Template Header with Title and Toggle */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div
                          style={{ cursor: 'pointer', flex: 1 }}
                          onClick={() => toggleGroup(templateId)}
                        >
                          <strong>{getTemplateName(templateId)}</strong>
                          <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>{getCampusName(testTemplates.find(t => t.id === templateId)?.campusId)}</span>
                          <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '4px' }}>
                            {totalResponses} response{totalResponses !== 1 ? 's' : ''} in {Object.keys(groupsData).length} group{Object.keys(groupsData).length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            padding: '0 8px'
                          }}
                          onClick={() => toggleGroup(templateId)}
                        >
                          {expandedGroups.has(templateId) ? '\u2212' : '+'}
                        </div>
                      </div>

                      {/* Action Buttons in Multiple Rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {/* Row 0: 考试批次选择（影响下方response列表、报告生成与排名查看的范围） */}
                        <T.ActionRow style={{ gap: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Exam Batch:</span>
                          <SearchableSelect
                            size="sm"
                            aria-label="Exam batch"
                            clearable={false}
                            menuMinWidth={180}
                            style={{ width: '200px', flex: '0 1 200px' }}
                            value={getSelectedBatchId(templateId)}
                            onChange={(batchId) => setSelectedBatchByTemplate(prev => ({
                              ...prev,
                              [templateId]: batchId || 'all'
                            }))}
                            options={[
                              { value: 'all', label: 'All Batches' },
                              ...getTemplateBatches(templateId).map(batch => ({
                                value: batch.id,
                                label: `${batch.name}${batch.id === testTemplates.find(t => t.id === templateId)?.currentBatchId ? ' (current)' : ''}`,
                              })),
                            ]}
                          />
                          {canManageTestBatches(highestRole) && (
                            <S.Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openBatchModal(templateId);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: '#6f42c1',
                                color: 'white',
                                whiteSpace: 'nowrap',
                                border: 'none'
                              }}
                            >
                              Manage Batches
                            </S.Button>
                          )}
                          {getTemplateBatches(templateId).length === 0 && (
                            <span style={{ fontSize: '11px', color: '#dc3545' }}>
                              No batches defined - create one before entering new responses
                            </span>
                          )}
                        </T.ActionRow>

                        {/* Row 1: Generate Buttons */}
                        <T.ActionRow>
                          {canGenerateTestReport(highestRole) && activeTab === 'finalExam' && (
                            <S.Button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateReportForTemplate(templateId);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: disabledReportButtons.has(`template-${templateId}`) ? '#6c757d' : '#ff6b35',
                                color: 'white',
                                whiteSpace: 'nowrap',
                                cursor: disabledReportButtons.has(`template-${templateId}`) ? 'not-allowed' : 'pointer',
                                opacity: disabledReportButtons.has(`template-${templateId}`) ? 0.6 : 1,
                                border: 'none',
                                boxShadow: disabledReportButtons.has(`template-${templateId}`) ? 'none' : '0 2px 4px rgba(255, 107, 53, 0.3)'
                              }}
                              disabled={disabledReportButtons.has(`template-${templateId}`)}
                            >
                              Generate Report for All Groups
                            </S.Button>
                          )}
                          {/* Mock Test report generation */}
                          {canGenerateTestReport(highestRole) && activeTab === 'mockTest' && (
                            <T.ActionRow style={{ gap: '8px' }}>
                              <ChipSelect
                                size="sm"
                                aria-label="Transformation method"
                                value={transformationMethods[templateId] || 'statistical'}
                                onChange={(method) => setTransformationMethods(prev => ({
                                  ...prev,
                                  [templateId]: method
                                }))}
                                options={[
                                  { value: 'statistical', label: 'Statistical' },
                                  { value: 'traditional', label: 'Traditional' },
                                ]}
                              />
                              <S.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generateReportForTemplate(templateId, transformationMethods[templateId] || 'statistical');
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  background: disabledReportButtons.has(`template-${templateId}`) ? '#6c757d' : '#ff6b35',
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  cursor: disabledReportButtons.has(`template-${templateId}`) ? 'not-allowed' : 'pointer',
                                  opacity: disabledReportButtons.has(`template-${templateId}`) ? 0.6 : 1,
                                  border: 'none',
                                  boxShadow: disabledReportButtons.has(`template-${templateId}`) ? 'none' : '0 2px 4px rgba(255, 107, 53, 0.3)'
                                }}
                                disabled={disabledReportButtons.has(`template-${templateId}`)}
                              >
                                Generate Mock Test Reports for All Groups
                              </S.Button>
                            </T.ActionRow>
                          )}

                          {canViewTestRanking(highestRole) && (
                            <S.Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openRankingModal(templateId);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: '#17a2b8',
                                color: 'white',
                                whiteSpace: 'nowrap',
                                border: 'none'
                              }}
                            >
                              View Full Rankings (Internal)
                            </S.Button>
                          )}

                          {isLocalhost && canGenerateTestReport(highestRole) && activeTab === 'finalExam' && (
                            <S.Button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateDataOnlyForTemplate(templateId);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: '#ff9800',
                                color: 'white',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Generate Data Only
                            </S.Button>
                          )}
                        </T.ActionRow>

                        {/* Row 2: Download Buttons - only for finalExam */}
                        {activeTab === 'finalExam' && (() => {
                          const template = testTemplates.find(t => t.id === templateId);
                          const templateZipUrls = getTemplateZipUrls(template);

                          if (templateZipUrls.hasMasterZip || templateZipUrls.hasRegularZips) {
                            return (
                              <T.ActionRow>
                                {/* Master ZIP Download (All Groups Organized) */}
                                {templateZipUrls.hasMasterZip && (
                                  <S.Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadMasterZipReport(template);
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      background: '#28a745',
                                      color: 'white',
                                      whiteSpace: 'nowrap'
                                    }}
                                    disabled={isLoading}
                                  >
                                    Download All Reports (Organized)
                                  </S.Button>
                                )}
                              </T.ActionRow>
                            );
                          }
                          return null;
                        })()}
                        {/* Mock Test downloads */}
                        {activeTab === 'mockTest' && (() => {
                          const template = testTemplates.find(t => t.id === templateId);
                          const templateZipUrls = getTemplateZipUrls(template);

                          if (templateZipUrls.hasMasterZip) {
                            return (
                              <T.ActionRow>
                                {/* Master ZIP Download (All Groups Organized) */}
                                {templateZipUrls.hasMasterZip && (
                                  <S.Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadMasterZipReport(template);
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      background: '#28a745',
                                      color: 'white',
                                      whiteSpace: 'nowrap'
                                    }}
                                    disabled={isLoading}
                                  >
                                    Download All Mock Test Reports (Organized)
                                  </S.Button>
                                )}
                              </T.ActionRow>
                            );
                          }
                          return null;
                        })()}

                        {/* Row: Archive/Unarchive Button */}
                        {canArchiveResponses(highestRole) && (
                          <T.ActionRow>
                            {showArchivedResponses ? (
                              <S.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveTemplateResponses(templateId, false);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  background: '#17a2b8',
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  border: 'none'
                                }}
                              >
                                Unarchive All Responses
                              </S.Button>
                            ) : (
                              <S.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveTemplateResponses(templateId, true);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  background: '#6c757d',
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  border: 'none'
                                }}
                              >
                                Archive All Responses
                              </S.Button>
                            )}
                          </T.ActionRow>
                        )}
                      </div>
                    </div>

                    {/* 展开的group分组列表 */}
                    {expandedGroups.has(templateId) && (
                      <div style={{ marginLeft: '20px' }}>
                        {Object.entries(groupsData)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([groupName, responses]) => {
                          // Sort responses within each group alphabetically by student name
                          const sortedResponses = [...responses].sort((a, b) => a.studentName.localeCompare(b.studentName));
                          const groupKey = `${templateId}-${groupName}`;
                          const template = testTemplates.find(t => t.id === templateId);
                          const groupZipUrls = getGroupZipUrls(template, groupName);

                          return (
                            <div key={groupKey} style={{ marginBottom: '15px' }}>
                              {/* 二级分组标题 (Group级别) */}
                              <T.GroupHeader
                                style={{
                                  marginBottom: expandedGroups.has(groupKey) ? '8px' : '0'
                                }}
                              >
                                <div
                                  style={{ flex: 1 }}
                                  onClick={() => toggleGroup(groupKey)}
                                >
                                  <strong style={{ color: '#856404', fontSize: '14px' }}>
                                    {groupName}
                                  </strong>
                                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                                    {sortedResponses.length} response{sortedResponses.length !== 1 ? 's' : ''}
                                  </div>
                                </div>
                                <T.GroupActions>
                                  {groupName === 'No Group' ? (
                                    <div style={{
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      color: '#6c757d',
                                      fontStyle: 'italic',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      Please add to a group for report generation
                                    </div>
                                  ) : (
                                    <>
                                      {canGenerateTestReport(highestRole) && activeTab === 'finalExam' && (
                                        <S.Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            generateGroupResponsesReport(templateId, groupName);
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            background: disabledReportButtons.has(groupKey) ? '#6c757d' : '#ff6b35',
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            cursor: disabledReportButtons.has(groupKey) ? 'not-allowed' : 'pointer',
                                            opacity: disabledReportButtons.has(groupKey) ? 0.6 : 1,
                                            border: 'none',
                                            boxShadow: disabledReportButtons.has(groupKey) ? 'none' : '0 2px 4px rgba(255, 107, 53, 0.3)'
                                          }}
                                          disabled={disabledReportButtons.has(groupKey)}
                                        >
                                          Generate Report for {groupName}
                                        </S.Button>
                                      )}

                                      {isLocalhost && canGenerateTestReport(highestRole) && activeTab === 'finalExam' && (
                                        <S.Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            generateDataOnlyForGroup(templateId, groupName);
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            background: '#ff9800',
                                            color: 'white',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          Generate Data Only
                                        </S.Button>
                                      )}
                                      {/* Group Zip Download Buttons - only for finalExam */}
                                      {activeTab === 'finalExam' && (
                                        <>
                                          <S.Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (groupZipUrls.regularZipUrl) {
                                                downloadGroupZipReport(groupZipUrls.regularZipUrl, getTemplateName(templateId), groupName);
                                              }
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              fontSize: '11px',
                                              background: groupZipUrls.regularZipUrl ? '#28a745' : '#6c757d',
                                              color: 'white',
                                              whiteSpace: 'nowrap',
                                              cursor: groupZipUrls.regularZipUrl ? 'pointer' : 'not-allowed',
                                              opacity: groupZipUrls.regularZipUrl ? 1 : 0.6
                                            }}
                                            disabled={!groupZipUrls.regularZipUrl || isLoading}
                                          >
                                            Download Group-Wise Reports
                                          </S.Button>

                                          {/* All-Groups Zip Download Button */}
                                          <S.Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadGroupAllGroupsZip(template, groupName);
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              fontSize: '11px',
                                              background: groupZipUrls.allGroupsZipUrl ? '#17a2b8' : '#6c757d',
                                              color: 'white',
                                              whiteSpace: 'nowrap',
                                              cursor: groupZipUrls.allGroupsZipUrl ? 'pointer' : 'not-allowed',
                                              opacity: groupZipUrls.allGroupsZipUrl ? 1 : 0.6
                                            }}
                                            disabled={!groupZipUrls.allGroupsZipUrl || isLoading}
                                          >
                                            Download All-Groups Reports
                                          </S.Button>
                                        </>
                                      )}
                                      {/* Mock Test download buttons */}
                                      {activeTab === 'mockTest' && (
                                        <>
                                          {/* All-Groups Zip Download Button */}
                                          <S.Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadGroupAllGroupsZip(template, groupName);
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              fontSize: '11px',
                                              background: groupZipUrls.allGroupsZipUrl ? '#17a2b8' : '#6c757d',
                                              color: 'white',
                                              whiteSpace: 'nowrap',
                                              cursor: groupZipUrls.allGroupsZipUrl ? 'pointer' : 'not-allowed',
                                              opacity: groupZipUrls.allGroupsZipUrl ? 1 : 0.6
                                            }}
                                            disabled={!groupZipUrls.allGroupsZipUrl || isLoading}
                                          >
                                            Download All-Groups Mock Test Reports
                                          </S.Button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </T.GroupActions>

                                {/* +/- \u5f00\u5173\u5fc5\u987b\u662f GroupHeader \u7684\u7b2c 3 \u4e2a\u5b50\u5143\u7d20\uff1a
                                    \u624b\u673a\u7aef grid \u5e03\u5c40\u636e\u6b64\u628a\u5b83\u653e\u5728\u6807\u9898\u540c\u4e00\u884c\u7684\u53f3\u4fa7 */}
                                <div
                                  style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#856404',
                                    cursor: 'pointer',
                                    padding: '0 8px',
                                    lineHeight: 1.4
                                  }}
                                  onClick={() => toggleGroup(groupKey)}
                                >
                                  {expandedGroups.has(groupKey) ? '\u2212' : '+'}
                                </div>
                              </T.GroupHeader>

                              {/* 展开的响应列表 */}
                              {expandedGroups.has(groupKey) && (
                                <S.ItemList style={{ marginLeft: '15px' }}>
                                  {sortedResponses.map((response) => (
                                    <T.ListRow key={response.id}>
                                      <T.RowMeta>
                                        <S.ItemText>{response.studentName}</S.ItemText>
                                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                          Created: {new Date(response.created_at).toLocaleDateString()} |
                                          Creator: {response.creator}
                                          {(response.responseGroup || response.testResponse?.group || response.group) && <span> | Group: {response.responseGroup || response.testResponse?.group || response.group}</span>}
                                          {response.batchId ? (
                                            <span style={{
                                              marginLeft: '8px',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              backgroundColor: '#6f42c1',
                                              color: 'white'
                                            }}>
                                              {getBatchName(templateId, response.batchId) || 'Unknown Batch'}
                                            </span>
                                          ) : (
                                            <span style={{
                                              marginLeft: '8px',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              backgroundColor: '#ffc107',
                                              color: '#212529'
                                            }}>
                                              No Batch
                                            </span>
                                          )}
                                          {activeTab === 'mockTest' && response.reportMethod && (
                                            <span style={{
                                              marginLeft: '8px',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              backgroundColor: response.reportMethod === 'statistical' ? '#17a2b8' : '#6f42c1',
                                              color: 'white'
                                            }}>
                                              {response.reportMethod === 'statistical' ? 'Statistical' : 'Traditional'}
                                            </span>
                                          )}
                                        </div>
                                      </T.RowMeta>
                                        <T.RowActions>
                                          {/* Row 1: Edit and Delete Buttons */}
                                          <T.ActionRow>
                                            <S.Button
                                              onClick={() => editTestResponse(response.id)}
                                              style={{ padding: '4px 8px', fontSize: '12px' }}
                                              disabled={isLoading}
                                            >
                                              Edit
                                            </S.Button>
                                            {canDeleteTestResponse(highestRole) && (
                                              <S.Button
                                                onClick={() => deleteTestResponse(response.id, response.studentName)}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '12px',
                                                  background: '#dc3545',
                                                  color: 'white'
                                                }}
                                                disabled={isLoading}
                                              >
                                                Delete
                                              </S.Button>
                                            )}
                                          </T.ActionRow>

                                          {/* Row 2: Group-Specific Reports - only for finalExam */}
                                          {activeTab === 'finalExam' && (
                                            <T.ActionRow>
                                              <S.Button
                                                onClick={() => response.pdfUrl ? downloadPdfReport(response.pdfUrl, response.studentName, 'group') : null}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '12px',
                                                  background: response.pdfUrl ? '#28a745' : '#6c757d',
                                                  color: 'white',
                                                  cursor: response.pdfUrl ? 'pointer' : 'not-allowed',
                                                  opacity: response.pdfUrl ? 1 : 0.6,
                                                  whiteSpace: 'nowrap'
                                                }}
                                                disabled={!response.pdfUrl || isLoading}
                                              >
                                                {response.pdfUrl ? 'Download Group-Wise Report' : 'No Group Report'}
                                              </S.Button>
                                            </T.ActionRow>
                                          )}
                                          {/* Mock Test reports */}
                                          {activeTab === 'mockTest' && (
                                            <T.ActionRow>
                                              <S.Button
                                                onClick={() => response.pdfUrlGroup ? downloadPdfReport(response.pdfUrlGroup, response.studentName, 'mock_test') : null}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '12px',
                                                  background: response.pdfUrlGroup ? '#28a745' : '#6c757d',
                                                  color: 'white',
                                                  cursor: response.pdfUrlGroup ? 'pointer' : 'not-allowed',
                                                  opacity: response.pdfUrlGroup ? 1 : 0.6,
                                                  whiteSpace: 'nowrap'
                                                }}
                                                disabled={!response.pdfUrlGroup || isLoading}
                                              >
                                                {response.pdfUrlGroup ? 'Download Mock Test Report' : 'No Mock Test Report'}
                                              </S.Button>
                                            </T.ActionRow>
                                          )}

                                          {/* Row 3: All-Groups Reports - only for finalExam */}
                                          {activeTab === 'finalExam' && response.pdfUrlGroup && (
                                            <T.ActionRow>
                                              <S.Button
                                                onClick={() => downloadAllGroupsReport(response, true)}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '12px',
                                                  background: '#17a2b8',
                                                  color: 'white',
                                                  whiteSpace: 'nowrap'
                                                }}
                                                disabled={isLoading}
                                              >
                                                Download All-Groups Report
                                              </S.Button>
                                            </T.ActionRow>
                                          )}
                                        </T.RowActions>
                                    </T.ListRow>
                                  ))}
                                </S.ItemList>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
                  })}
                </div>
              ) : (
                <S.EmptyState>
                  <h3>No {getTemplateTypeDisplayName(activeTab)} Responses Yet</h3>
                  <p>Create your first {activeTab === 'finalExam' ? 'final exam' : 'mock test'} response to get started.</p>
                  {canEditTestResponse(highestRole) && (
                    <S.Button onClick={createNewTestResponse} style={{ marginTop: '10px' }}>
                      Create Your First {activeTab === 'finalExam' ? 'Final Exam' : 'Mock Test'} Response
                    </S.Button>
                  )}
                </S.EmptyState>
              )}
            </>
          )}
        </S.Section>
      )}

      {/* Report Queue Floating Panel */}
      {canViewReportQueue && (
        <>
          {/* Queue Toggle Button - Hidden when queue panel is open */}
          {!showQueue && (
            <T.QueueTab onClick={() => setShowQueue(!showQueue)}>
              Report Queue ({reportQueue.length}) {queueRefreshInterval.current && ''}
            </T.QueueTab>
          )}

          {/* Queue Panel */}
          {showQueue && (
            <T.QueuePanel>
              {/* Panel Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Report Generation Queue</h3>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
                    Auto-refreshing every 10s
                  </div>
                </div>
                <button
                  onClick={() => setShowQueue(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}
                >
                  x
                </button>
              </div>

              {/* Panel Content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                {queueLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>Loading queue...</div>
                  </div>
                ) : (
                  <>
                    {/* Real Data Section */}
                    {reportQueue.length > 0 ? (
                      <div style={{ borderBottom: '2px solid #e9ecef' }}>
                        <div
                          style={{
                            background: '#f8f9fa',
                            padding: '12px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#495057',
                            borderBottom: '1px solid #dee2e6',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                          }}
                        >
                          Active Queue ({reportQueue.length})
                        </div>
                        {reportQueue.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              padding: '16px 20px',
                              borderBottom: '1px solid #e9ecef',
                              background: 'white'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <div style={{ flex: 1, marginRight: '10px' }}>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                                  {item.templateName}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                                  Group: {item.targetGroup || 'N/A'}
                                  {item.batchName && <span> | Batch: {item.batchName}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: getStatusColor(item.queueStatus),
                                      color: 'white',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {item.queueStatus?.toUpperCase() || 'UNKNOWN'}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: '#6c757d',
                                      color: 'white',
                                    }}
                                  >
                                    {item.processMode}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => deleteQueueItem(item.id)}
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                Delete
                              </button>
                            </div>

                            {/* Progress and Results */}
                            <div style={{ background: '#f8f9fa', padding: '8px', borderRadius: '4px', fontSize: '12px', marginBottom: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>Progress: <strong>{item.processedStudents} / {item.totalStudents}</strong></span>
                                {item.queueStatus === 'completed' && item.elapsed_time && (
                                  <span>Time: <strong>{item.elapsed_time}s</strong></span>
                                )}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{color: '#28a745'}}>Success: <strong>{item.successfulReports}</strong></span>
                                <span style={{color: '#dc3545'}}>Failed: <strong>{item.failedReports}</strong></span>
                              </div>
                              {item.error_message && (
                                <div style={{ marginTop: '6px', color: '#dc3545', fontSize: '11px', wordBreak: 'break-all' }}>
                                  <strong>Error:</strong> {item.error_message}
                                </div>
                              )}
                            </div>
                            {/* Timestamps */}
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                              <div>Created: {new Date(item.created_at + 'Z').toLocaleString('en-AU', {timeZone: 'Asia/Shanghai'})} by {item.creator}</div>
                              {item.completed_at && <div>Completed: {new Date(item.completed_at + 'Z').toLocaleString('en-AU', {timeZone: 'Asia/Shanghai'})}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
                        <div style={{ fontSize: '16px', marginBottom: '8px' }}>Queue</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>No Active Queue Items</div>
                        <div style={{ fontSize: '12px' }}>Report generation queue is empty</div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Panel Footer */}
              <div
                style={{
                  background: '#f8f9fa',
                  padding: '16px 20px',
                  borderTop: '1px solid #dee2e6'
                }}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={fetchReportQueue}
                    disabled={queueLoading}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #3498db, #2980b9)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: queueLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: queueLoading ? 0.6 : 1
                    }}
                  >
                    {queueLoading ? 'Refreshing...' : 'Refresh Queue'}
                  </button>
                  <button
                    onClick={deleteAllQueueItems}
                    disabled={queueLoading || reportQueue.length === 0}
                    style={{
                      flex: 1,
                      background: reportQueue.length === 0 ? '#6c757d' : 'linear-gradient(135deg, #e74c3c, #c0392b)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: (queueLoading || reportQueue.length === 0) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: (queueLoading || reportQueue.length === 0) ? 0.6 : 1
                    }}
                  >
                    Delete All ({reportQueue.length})
                  </button>
                </div>
              </div>
            </T.QueuePanel>
          )}
        </>
      )}

      {/* Floating Loading Indicator for Responses */}
      {isLoadingResponses && (
        <>
          <style>
            {`
              @keyframes response-loader-spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }

              @keyframes response-loader-pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.8; transform: scale(1.05); }
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1500,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '25px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'response-loader-pulse 1.5s ease-in-out infinite',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'response-loader-spin 1s linear infinite'
              }}
            />
            Loading responses...
          </div>
        </>
      )}

      {/* Internal Full Rankings Modal (backdrop click intentionally does NOT close) */}
      {rankingModalTemplateId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '1100px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  Full Score Rankings{rankingData?.templateName ? ` - ${rankingData.templateName}` : ''}
                </h3>
                <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                  Internal view only. Parent-facing PDF reports show ranks for the top 10 only.
                  {(() => {
                    const selectedBatch = getSelectedBatchId(rankingModalTemplateId);
                    return selectedBatch !== 'all'
                      ? ` Batch: ${getBatchName(rankingModalTemplateId, selectedBatch) || selectedBatch}.`
                      : ' All current (non-archived) students.';
                  })()}
                </div>
              </div>
              <button
                onClick={closeRankingModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '0 20px 16px', overflow: 'auto', flex: 1 }}>
              {rankingLoading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  Loading rankings...
                </div>
              )}
              {rankingError && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
                  {rankingError}
                </div>
              )}
              {!rankingLoading && !rankingError && rankingData && rankingData.students.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  No scored responses found for this template.
                </div>
              )}
              {!rankingLoading && !rankingError && rankingData && rankingData.students.length > 0 && (() => {
                const groupNames = [...new Set(rankingData.students.map(s => s.group))].sort();
                const filteredStudents = rankingGroupFilter === 'all'
                  ? rankingData.students
                  : [...rankingData.students]
                      .filter(s => s.group === rankingGroupFilter)
                      .sort((a, b) => (a.groupRank || 0) - (b.groupRank || 0));
                const showGroupView = rankingGroupFilter !== 'all';

                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 12px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        {rankingData.totalStudents} student{rankingData.totalStudents !== 1 ? 's' : ''} in total.
                        Rows highlighted in green are visible to parents (top 10 in report).
                      </div>
                      {groupNames.length > 1 && (
                        <SearchableSelect
                          size="sm"
                          aria-label="Ranking group"
                          clearable={false}
                          menuMinWidth={220}
                          style={{ width: '240px', maxWidth: '100%' }}
                          value={rankingGroupFilter}
                          onChange={(groupName) => setRankingGroupFilter(groupName || 'all')}
                          options={[
                            { value: 'all', label: 'All Groups (report ranking)' },
                            ...groupNames.map(g => ({ value: g, label: g })),
                          ]}
                        />
                      )}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {showGroupView ? 'Group Rank' : 'Rank'}
                          </th>
                          <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'left' }}>Student</th>
                          <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'left' }}>Group</th>
                          {!showGroupView && (
                            <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'center', whiteSpace: 'nowrap' }}>Group Rank</th>
                          )}
                          <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'center', whiteSpace: 'nowrap' }}>Total Score</th>
                          <th style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'center' }}>%</th>
                          {rankingData.sectionNames.map(sec => (
                            <th key={sec} style={{ padding: '8px', border: '1px solid #dee2e6', ...rankingStickyTh, textAlign: 'center' }}>{sec}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map(student => {
                          const visibleToParents = student.overallRank <= 10;
                          return (
                            <tr
                              key={`${student.studentName}-${student.group}`}
                              style={{ background: visibleToParents ? '#eaf7ea' : 'white' }}
                            >
                              <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: '600' }}>
                                {showGroupView ? student.groupRank : student.overallRank}
                              </td>
                              <td style={{ padding: '6px 8px', border: '1px solid #dee2e6' }}>{student.studentName}</td>
                              <td style={{ padding: '6px 8px', border: '1px solid #dee2e6' }}>{student.group}</td>
                              {!showGroupView && (
                                <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                  {student.groupRank} / {student.totalInGroup}
                                </td>
                              )}
                              <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                {student.totalScore} / {student.maxScore}
                              </td>
                              <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                {student.percentage}%
                              </td>
                              {rankingData.sectionNames.map(sec => {
                                const secData = student.sections[sec];
                                return (
                                  <td key={sec} style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    {secData
                                      ? `${secData.score} (#${showGroupView ? secData.groupRank : secData.overallRank})`
                                      : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e9ecef', textAlign: 'right' }}>
              <S.Button
                onClick={closeRankingModal}
                style={{ padding: '6px 16px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none' }}
              >
                Close
              </S.Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Batches Modal (backdrop click intentionally does NOT close) */}
      {batchModalTemplateId && (() => {
        const template = testTemplates.find(t => t.id === batchModalTemplateId);
        const batches = template?.batches || [];
        const currentBatchId = template?.currentBatchId;

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '620px',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden'
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #6f42c1 0%, #4b2d83 100%)',
                  color: 'white',
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    Manage Exam Batches{template ? ` - ${template.templateName}` : ''}
                  </h3>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                    Each batch represents one sitting of this exam. New responses default to the current batch.
                  </div>
                </div>
                <button
                  onClick={closeBatchModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '16px 20px', overflow: 'auto', flex: 1 }}>
                {/* Create new batch */}
                <T.ModalFormRow>
                  <input
                    type="text"
                    value={newBatchName}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    placeholder='New batch name (e.g. "2026-07" or "2026 Winter Mock")'
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #ccc'
                    }}
                  />
                  <S.Button
                    onClick={handleCreateBatch}
                    disabled={batchActionLoading}
                    style={{ padding: '8px 14px', fontSize: '13px', background: '#28a745', color: 'white', whiteSpace: 'nowrap' }}
                  >
                    Create Batch (set as current)
                  </S.Button>
                </T.ModalFormRow>

                {batches.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#6c757d', fontSize: '13px' }}>
                    No batches yet. Create the first batch for the upcoming exam sitting.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'left' }}>Batch</th>
                        <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center', whiteSpace: 'nowrap' }}>Responses (loaded)</th>
                        <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...batches].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '')).map(batch => (
                        <tr key={batch.id}>
                          <td style={{ padding: '6px 8px', border: '1px solid #dee2e6' }}>
                            {renamingBatchId === batch.id ? (
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <input
                                  type="text"
                                  value={renameBatchName}
                                  onChange={(e) => setRenameBatchName(e.target.value)}
                                  style={{ flex: 1, padding: '4px 6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <S.Button
                                  onClick={handleRenameBatch}
                                  disabled={batchActionLoading}
                                  style={{ padding: '4px 8px', fontSize: '11px', background: '#28a745', color: 'white' }}
                                >
                                  Save
                                </S.Button>
                                <S.Button
                                  onClick={() => { setRenamingBatchId(null); setRenameBatchName(''); }}
                                  style={{ padding: '4px 8px', fontSize: '11px', background: '#6c757d', color: 'white' }}
                                >
                                  Cancel
                                </S.Button>
                              </div>
                            ) : (
                              <strong>{batch.name}</strong>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                            {getBatchResponseCount(batchModalTemplateId, batch.id)}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                            {batch.id === currentBatchId ? (
                              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: '#28a745', color: 'white' }}>
                                Current
                              </span>
                            ) : (
                              <span style={{ fontSize: '11px', color: '#6c757d' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #dee2e6', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {batch.id !== currentBatchId && (
                              <S.Button
                                onClick={() => handleSetCurrentBatch(batch.id)}
                                disabled={batchActionLoading}
                                style={{ padding: '3px 8px', fontSize: '11px', background: '#007bff', color: 'white', marginRight: '4px' }}
                              >
                                Set Current
                              </S.Button>
                            )}
                            <S.Button
                              onClick={() => { setRenamingBatchId(batch.id); setRenameBatchName(batch.name); }}
                              disabled={batchActionLoading}
                              style={{ padding: '3px 8px', fontSize: '11px', background: '#17a2b8', color: 'white', marginRight: '4px' }}
                            >
                              Rename
                            </S.Button>
                            <S.Button
                              onClick={() => handleDeleteBatch(batch.id, batch.name)}
                              disabled={batchActionLoading}
                              style={{ padding: '3px 8px', fontSize: '11px', background: '#dc3545', color: 'white' }}
                            >
                              Delete
                            </S.Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ marginTop: '12px', fontSize: '12px', color: '#6c757d', lineHeight: 1.5 }}>
                  <div>• Report ranking & distribution use only the selected batch; mock test predicted scores always use all batches of this template.</div>
                  <div>• "Responses (loaded)" counts only responses currently loaded in this page (enable "Show Archived" to load past batches).</div>
                  <div>• A batch can only be deleted when no responses belong to it.</div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '12px 20px', borderTop: '1px solid #e9ecef', textAlign: 'right' }}>
                <S.Button
                  onClick={closeBatchModal}
                  style={{ padding: '6px 16px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none' }}
                >
                  Close
                </S.Button>
              </div>
            </div>
          </div>
        );
      })()}

    </T.Page>
  );
};

export default TestManagement;
